import { useEffect, useRef } from 'react';

import {
  type ExampleQueryClient,
  resetExampleGroupQueries,
} from '../fixtures/exampleGroupQueries';
import { resetExampleSandbox } from '../fixtures/exampleSandbox';
import type { ExampleScenario } from '../fixtures/exampleScenarios';

/**
 * The example group's per-launch bookkeeping, shared by web and mobile
 * (each app mounts a tiny component that feeds this its own language hook,
 * query client and store action).
 *
 * Once per JS process — i.e. per app launch / page load:
 * - records the launch (the example rotates one scenario per launch;
 *   `selectExampleScenario` maps the count to a scenario), unless the host
 *   says not to (web skips it when the page loads on an example route, so
 *   a reload never swaps the group under the URL);
 * - drops the example's cached queries, so the persisted cache can't hand
 *   the previous launch's scenario to a screen;
 * - empties the example's sandbox and clears a chip-picked scenario.
 *
 * Afterwards, drops them again on every language change: the fixture is
 * generated per language at the API client, the list re-derives from a
 * memo keyed on `language`, but detail screens read the cache. A change of
 * the chip-picked scenario drops them too, so the screen re-reads that
 * scenario with its own adds (the sandbox keeps every scenario's adds and
 * the keep offer until the next launch).
 *
 * Also keeps the example's hide flag in step with the server: the loaded
 * profile's value is mirrored into the store whenever it is defined (an
 * undefined value — no user loaded yet — leaves the device's own value
 * alone; hosts resolve a loaded user's unset flag to `false`, so one
 * account's hide never carries over to the next), and the app's API
 * client is registered as the store's write-through persister so the
 * user's own hide/show reaches their other devices.
 *
 * "Once per process" is a MODULE-LEVEL guard, not a ref: an error boundary
 * above the host component can unmount and remount it inside the same
 * page load, and a ref would count that as a second launch — rotating the
 * scenario under the user and skipping one in the cycle.
 */

let launchHandledThisProcess = false;

/** Tests only: forget that a launch was handled in this process. */
export function __resetExampleGroupSessionForTests(): void {
  launchHandledThisProcess = false;
}

export interface UseExampleGroupSessionOptions {
  /** Current UI language; a change resets the example's cached queries. */
  language: string;
  queryClient: ExampleQueryClient;
  /** Increments the persisted launch counter in the UI store. */
  recordLaunch: () => void;
  /**
   * Whether this launch should advance the rotation. Defaults to yes;
   * web answers no when the page loaded on an example route.
   */
  shouldRecordLaunch?: () => boolean;
  /** `exampleGroupHidden` as the loaded profile reports it (`false` if unset); undefined only while no user is loaded. */
  serverHidden?: boolean;
  /** The store's mirror-only setter (`applyServerExampleGroupHidden`). */
  applyServerHidden?: (hidden: boolean) => void;
  /** Writes the user's own hide/show to the server (a settings PUT). */
  persistHidden?: (hidden: boolean) => Promise<unknown>;
  /** The store's persister registration (`setExampleGroupHiddenPersister`). */
  registerPersister?: (
    persist: ((hidden: boolean) => Promise<unknown>) | null
  ) => void;
  /** The UI store's `exampleScenarioOverride`. */
  scenarioOverride?: ExampleScenario['key'] | null;
  /** Clears it (`setExampleScenarioOverride(null)`). */
  clearScenarioOverride?: () => void;
}

export function useExampleGroupSession({
  language,
  queryClient,
  recordLaunch,
  shouldRecordLaunch,
  serverHidden,
  applyServerHidden,
  persistHidden,
  registerPersister,
  scenarioOverride = null,
  clearScenarioOverride,
}: UseExampleGroupSessionOptions): void {
  const prevLanguage = useRef(language);
  const prevOverride = useRef(scenarioOverride);

  useEffect(() => {
    if (launchHandledThisProcess) return;
    launchHandledThisProcess = true;
    if (shouldRecordLaunch?.() ?? true) recordLaunch();
    clearScenarioOverride?.();
    resetExampleSandbox();
    void resetExampleGroupQueries(queryClient);
    // Runs once per process by construction; the deps only matter for the
    // first run, and re-running with new identities must stay a no-op.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (prevLanguage.current === language) return;
    prevLanguage.current = language;
    void resetExampleGroupQueries(queryClient);
  }, [language, queryClient]);

  useEffect(() => {
    if (prevOverride.current === scenarioOverride) return;
    prevOverride.current = scenarioOverride;
    void resetExampleGroupQueries(queryClient);
  }, [scenarioOverride, queryClient]);

  useEffect(() => {
    if (serverHidden === undefined) return;
    applyServerHidden?.(serverHidden);
  }, [serverHidden, applyServerHidden]);

  useEffect(() => {
    if (!persistHidden || !registerPersister) return;
    registerPersister(persistHidden);
    return () => registerPersister(null);
  }, [persistHidden, registerPersister]);
}
